import React from 'react';
import { observer } from 'mobx-react';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';

import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { brandConfig } from 'brand-config';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';

import useStyles from './sub-risk.styles';

const radioOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const DEFAULT_ABOVE_GROUND_POOL_LIMIT = 2500;

const SwimmingPoolForm = ({ fieldName, disabled }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();

  const poolType = getIn(values, `${fieldName}.building.swimmingPool.typeOfPool`);

  // Get brand-configured default for above-ground pool limit (GitHub issue #29233)
  const locationFieldName = fieldName.split('.risks')[0];
  const state = getIn(values, `${locationFieldName}.address.state`);
  const aboveGroundPoolDefaultLimit =
    brandConfig.config?.farm?.[state]?.aboveGroundPoolDefaultLimit ?? DEFAULT_ABOVE_GROUND_POOL_LIMIT;

  const getRadioValue = (value) => {
    if (value === null) {
      return undefined;
    }

    return value ? 'true' : 'false';
  };

  // Auto-expand when pool type is null or undefined (not yet answered)
  const shouldAutoExpand = poolType === null || poolType === undefined;

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Swimming Pools</div>}
      initialOpen={shouldAutoExpand}
    >
      <Grid container spacing={2}>
        <Field
          xs={12}
          type="select"
          id={`${fieldName}.building.swimmingPool.typeOfPool`}
          name={`${fieldName}.building.swimmingPool.typeOfPool`}
          mode="dark"
          label="Type Of Pool"
          options={lookupsJson.farm.swimmingPoolTypes}
          disabled={disabled}
          onChange={(val) => {
            if (val === 'N') {
              setFieldValue(`${fieldName}.coverages.poolLimit`, undefined);
              setFieldValue(`${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`, undefined);
              setFieldValue(`${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`, undefined);
              setFieldValue(`${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`, undefined);
            } else if (val === 'A') {
              // Default above-ground pool limit to brand config value (GitHub issue #29233)
              setFieldValue(`${fieldName}.coverages.poolLimit`, aboveGroundPoolDefaultLimit);
            } else if (val === 'I') {
              // Clear pool limit so user must explicitly enter a value for in-ground (GitHub issue #29233)
              setFieldValue(`${fieldName}.coverages.poolLimit`, undefined);
            }
          }}
        />

        {poolType === 'A' && (
          <Field
            xs={12}
            type="numeric"
            id={`${fieldName}.coverages.poolLimit`}
            name={`${fieldName}.coverages.poolLimit`}
            mode="dark"
            label="Additional increased limit"
            description={`Minimum: $${aboveGroundPoolDefaultLimit.toLocaleString()}, Included: $${aboveGroundPoolDefaultLimit.toLocaleString()}, Total: $${(Number(getIn(values, `${fieldName}.coverages.poolLimit`) || 0) + aboveGroundPoolDefaultLimit).toLocaleString()}`}
            descriptionClassName={classes.fieldHelperText}
            format={{ thousandSeparator: true, prefix: '$' }}
            disabled={disabled}
          />
        )}

        {poolType === 'I' && (
          <Grid item xs={12}>
            <LabelTooltip
              label="Coverage limit"
              tooltip={{
                label: 'More Info',
                onHoverText: 'To exclude swimming pool coverage, set the coverage limit to $0.'
              }}
            >
              <Field
                type="numeric"
                id={`${fieldName}.coverages.poolLimit`}
                name={`${fieldName}.coverages.poolLimit`}
                mode="dark"
                format={{ thousandSeparator: true, prefix: '$' }}
                disabled={disabled}
              />
            </LabelTooltip>
          </Grid>
        )}

        {poolType && poolType !== 'N' && (
          <>
            {poolType === 'I' ? (
              <>
                <Grid item xs={12}>
                  <Label type="infoValueLight" className={classes.radioLabel}>
                    Is there a minimum 4ft high fence with self-latching gate?
                  </Label>
                </Grid>
                <Field
                  xs={12}
                  type="radio"
                  id={`${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`}
                  name={`${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`}
                  mode="dark"
                  value={getRadioValue(
                    getIn(values, `${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`)
                  )}
                  onChange={(e) => {
                    setFieldValue(
                      `${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`,
                      e.target.value === 'true'
                    );
                  }}
                  options={radioOptions}
                  disabled={disabled}
                />
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Label type="infoValueLight" className={classes.radioLabel}>
                    Are there removable steps or a locking gate with decking around pool?
                  </Label>
                </Grid>
                <Field
                  xs={12}
                  type="radio"
                  id={`${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`}
                  name={`${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`}
                  mode="dark"
                  value={getRadioValue(
                    getIn(values, `${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`)
                  )}
                  onChange={(e) => {
                    setFieldValue(
                      `${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`,
                      e.target.value === 'true'
                    );
                  }}
                  options={radioOptions}
                  disabled={disabled}
                />
              </>
            )}

            <Grid item xs={12}>
              <Label type="infoValueLight" className={classes.radioLabel}>
                Is there a diving board or pool slide?
              </Label>
            </Grid>
            <Field
              xs={12}
              type="radio"
              id={`${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`}
              name={`${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`}
              mode="dark"
              value={getRadioValue(getIn(values, `${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`))}
              onChange={(e) => {
                setFieldValue(
                  `${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`,
                  e.target.value === 'true'
                );
              }}
              options={radioOptions}
              disabled={disabled}
            />
          </>
        )}
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

SwimmingPoolForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

SwimmingPoolForm.defaultProps = {
  disabled: false
};

export default observer(SwimmingPoolForm);
